import React, { useState, useRef, MouseEvent, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackSignupClick } from "../../utils/analytics";
import {
  motion,
  useScroll,
  useMotionValue,
  useMotionTemplate,
  useTransform,
  useSpring,
  AnimatePresence,
  MotionStyle,
  Transition,
} from "framer-motion";
import { commonVariants } from "@/types/animations";

interface AnimatedHamburgerIconProps {
  isOpen: boolean;
  toggle: () => void;
  color?: string;
  className?: string;
}

const lineVariants = (
  rotate: number,
  translateY: number,
  opacity: number = 1,
): any => ({
  closed: { rotate: 0, translateY: 0, opacity: 1 },
  open: { rotate, translateY, opacity },
});

const AnimatedHamburgerIcon: React.FC<AnimatedHamburgerIconProps> = ({
  isOpen,
  toggle,
  color = "#FFFFFF",
  className = "",
}) => {
  const transition: Transition = {
    type: "spring",
    stiffness: 260,
    damping: 20,
  };

  const lineStyle = {
    width: "24px",
    height: "2px",
    backgroundColor: color,
    borderRadius: "1px",
  };

  return (
    <motion.button
      onClick={toggle}
      className={`relative z-[110] flex flex-col justify-between focus:outline-none ${className}`}
      style={{
        width: "24px",
        height: "18px",
        background: "transparent",
        border: "none",
      }}
      animate={isOpen ? "open" : "closed"}
      whileTap={{ scale: 0.9 }}
      aria-label={isOpen ? "Close Menu" : "Open Menu"}
    >
      <motion.div
        style={{ ...lineStyle, originX: "center", originY: "0px" }}
        variants={lineVariants(45, 6)}
        transition={transition}
      />
      <motion.div
        style={lineStyle}
        variants={{ closed: { opacity: 1 }, open: { opacity: 0 } }}
        transition={{ duration: 0.1 }}
      />
      <motion.div
        style={{ ...lineStyle, originX: "center", originY: "2px" }}
        variants={lineVariants(-45, -6)}
        transition={transition}
      />
    </motion.button>
  );
};

const colors = {
  gradientStart: "#014D43",
  gradientEnd: "#037D6F",
  turquoise: "#26A69A", // Este es el color que usaremos para el botón Sign Up
  turquoiseGlow: "rgba(38, 166, 154, 0.3)",
  turquoiseStrongGlow: "rgba(38, 166, 154, 0.7)", // Usado para el boxShadow del botón Sign Up
  paleGreen: "#E6F2EF",
  white: "#FFFFFF",
  softGray: "#8C8C8C",
  whiteTransparent: "rgba(255, 255, 255, 0.7)",
  cursorGlowColorStart: "rgba(38, 166, 154, 0.1)",
  cursorGlowColorEnd: "rgba(3, 125, 111, 0)",
  mobileMenuBackground: "#111111",
  mobileMenuText: "#FFFFFF",
  mobileMenuButtonBorder: "#FFFFFF", // Color del borde para el botón Sign Up móvil
  mobileMenuButtonText: "#FFFFFF", // Color del texto para el botón Sign Up móvil
  mobileBackdropColor: "rgba(0, 0, 0, 0.6)",
};

// Usar variants predefinidos y tipados correctamente
const navbarVariants = commonVariants.navbar;
const navContentVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } },
} as const;
const navItemVariants = commonVariants.navItem;
const mobileMenuItemContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.15 },
  },
} as const;
const mobileMenuItemVariants = commonVariants.mobileMenuItem;

const MagneticLink: React.FC<{
  to: string;
  labelKey: string;
  defaultLabel: string;
  onClick?: () => void;
  className?: string;
}> = ({ to, labelKey, defaultLabel, onClick, className = "" }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLAnchorElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const springConfig = { stiffness: 150, damping: 15, mass: 0.1 };
  const magneticX = useSpring(0, springConfig);
  const magneticY = useSpring(0, springConfig);

  const handleMouseMove = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!ref.current) return;
    const { clientX, clientY } = e;
    const { height, width, left, top } = ref.current.getBoundingClientRect();
    const x = clientX - (left + width / 2);
    const y = clientY - (top + height / 2);
    magneticX.set(x * 0.4);
    magneticY.set(y * 0.4);
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    magneticX.set(0);
    magneticY.set(0);
    setIsHovered(false);
  };

  return (
    <motion.div variants={navItemVariants} className={`relative ${className}`}>
      <motion.div
        className="absolute inset-0 rounded-md -z-10"
        style={{ backgroundColor: colors.turquoiseGlow }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: isHovered ? 1 : 0, opacity: isHovered ? 1 : 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
      />
      <Link
        to={to}
        ref={ref}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
      >
        <motion.span
          className="relative inline-block text-white py-2 px-2 lg:px-4"
          style={{ x: magneticX, y: magneticY }}
          transition={{ type: "spring", stiffness: 120, damping: 10 }}
        >
          {t(labelKey, defaultLabel)}
        </motion.span>
      </Link>
    </motion.div>
  );
};

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); // Obtén la función navigate
  const { scrollY } = useScroll();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const backgroundOpacity = useTransform(scrollY, [0, 80], [1, 0.8]);
  const backdropBlur = useTransform(
    scrollY,
    [0, 80],
    ["blur(0px)", "blur(10px)"],
  );
  const navHeight = useTransform(scrollY, [0, 80], ["80px", "68px"]);
  const navBorderRadius = useTransform(scrollY, [0, 80], ["0px", "15px"]);

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, {
    stiffness: 300,
    damping: 30,
    mass: 0.5,
  });
  const smoothMouseY = useSpring(mouseY, {
    stiffness: 300,
    damping: 30,
    mass: 0.5,
  });
  const background = useMotionTemplate`radial-gradient(circle 450px at ${smoothMouseX}px ${smoothMouseY}px, ${colors.cursorGlowColorStart} 0%, ${colors.cursorGlowColorEnd} 70%)`;

  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    mouseX.set(event.clientX - rect.left);
    mouseY.set(event.clientY - rect.top);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    // Guardar preferencia de idioma
    localStorage.setItem("i18nextLng", lng);

    // Actualizar la URL con el nuevo idioma
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|es|fr)/, "");
    const newPath = `/${lng}${pathWithoutLang || "/"}`;
    navigate(newPath);

    closeMobileMenu();
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const navStyle: MotionStyle = {
    background: `linear-gradient(160deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
    opacity: backgroundOpacity,
    backdropFilter: backdropBlur,
    WebkitBackdropFilter: backdropBlur,
    height: navHeight,
    borderBottomLeftRadius: navBorderRadius,
    borderBottomRightRadius: navBorderRadius,
  };

  return (
    <>
      <motion.nav
        className="fixed md:sticky top-0 z-50 overflow-hidden shadow-2xl transform-gpu"
        style={navStyle}
        variants={navbarVariants}
        initial="hidden"
        animate="visible"
        onMouseMove={handleMouseMove}
      >
        <motion.div
          className="absolute inset-0 pointer-events-none -z-10"
          style={{ background }}
        />
        <motion.div
          className="container mx-auto px-4 md:px-6 lg:px-8 h-full flex justify-between items-center relative z-10"
          variants={navContentVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-10">
            <motion.div variants={navItemVariants} className="-ml-2 lg:-ml-4">
              <Link
                to="/"
                className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity"
                onClick={closeMobileMenu}
              >
                <img
                  src="/images/logo/LogoVettConsultTransparent.webp"
                  alt={t("common.appName", "VettConsult")}
                  className="h-8 sm:h-10 lg:h-12 w-auto"
                />
                <span className="text-white font-bold text-lg sm:text-xl lg:text-2xl font-display">
                  VettConsult
                </span>
              </Link>
            </motion.div>
            <div className="hidden lg:flex items-center space-x-1 xl:space-x-3 text-sm lg:text-base">
              <MagneticLink to="/" labelKey="navbar.home" defaultLabel="Home" />
              <MagneticLink
                to="/services"
                labelKey="navbar.services"
                defaultLabel="Services"
              />
              <MagneticLink
                to="/pricing"
                labelKey="navbar.pricing"
                defaultLabel="Pricing"
              />
              {/* About Us link removed from desktop nav per request - kept here for reference
              <MagneticLink
                to={`/${i18n.language}/about`}
                labelKey="navbar.about"
                defaultLabel="About"
              />
              */}
              <MagneticLink
                to={`/${i18n.language}/blog`}
                labelKey="navbar.blog"
                defaultLabel="Blog"
              />
              <MagneticLink
                to={`/${i18n.language}/contact`}
                labelKey="navbar.contact"
                defaultLabel="Contact"
              />
            </div>
          </div>

          <div className="hidden sm:flex items-center space-x-2 md:space-x-4 lg:space-x-6">
            <motion.div
              variants={navItemVariants}
              className="hidden md:flex space-x-2 lg:space-x-4 border-r border-white/25 pr-2 lg:pr-6"
            >
              {["en", "es", "fr"].map((lng) => (
                <motion.button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  className={`text-xs lg:text-sm font-semibold transition-colors duration-200 ${
                    i18n.language === lng
                      ? "text-white scale-110"
                      : "text-white/60 hover:text-white"
                  }`}
                  whileHover={{ scale: 1.2, color: colors.white }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10 }}
                >
                  {lng.toUpperCase()}
                </motion.button>
              ))}
            </motion.div>
            <motion.div variants={navItemVariants}>
              <motion.div
                whileHover={{ y: -2, scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300, damping: 10 }}
              >
                <Link
                  to="/login"
                  className="text-white/80 hover:text-white transition-colors duration-200 text-sm md:text-base font-medium"
                >
                  {t("buttons.login", "Login")}
                </Link>
              </motion.div>
            </motion.div>
            {/* Botón Sign Up de Escritorio Corregido */}
            <motion.div variants={navItemVariants}>
              <Link
                to="/signup"
                onClick={() => trackSignupClick(i18n.language, "navbar")}
              >
                <motion.button
                  style={{
                    background: colors.turquoise, // Usamos el color turquesa definido
                    boxShadow: `0 0 0px 0px ${colors.turquoiseStrongGlow}`,
                  }}
                  className="text-white px-3 sm:px-4 md:px-6 py-2 md:py-2.5 rounded-xl text-sm md:text-base font-semibold focus:outline-none transform-gpu"
                  whileHover={{
                    scale: 1.08,
                    boxShadow: `0 0 25px 5px ${colors.turquoiseStrongGlow}`,
                    filter: "brightness(1.15)",
                    y: -2,
                  }}
                  whileTap={{ scale: 0.95, filter: "brightness(0.9)" }}
                  transition={{ type: "spring", stiffness: 250, damping: 12 }}
                >
                  {t("buttons.signUp", "Sign Up")}
                </motion.button>
              </Link>
            </motion.div>
          </div>

          <div className="sm:hidden">
            <AnimatedHamburgerIcon
              isOpen={isMobileMenuOpen}
              toggle={toggleMobileMenu}
              color={colors.white}
            />
          </div>
        </motion.div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <div
            className="fixed z-[100] flex flex-col justify-start h-full w-64 pt-20 pb-8 px-6 overflow-y-auto shadow-2xl"
            style={{
              backgroundColor: colors.mobileMenuBackground,
              position: "fixed",
              top: "0",
              left: "0",
              height: "100vh",
              width: "256px",
              zIndex: 100,
              margin: 0,
            }}
          >
            <AnimatedHamburgerIcon
              isOpen={isMobileMenuOpen}
              toggle={closeMobileMenu}
              color={colors.mobileMenuText}
              className="absolute top-5 right-5 md:top-6 md:right-6"
            />

            <motion.div
              className="flex flex-col items-start space-y-5 md:space-y-6 w-full mt-8"
              variants={mobileMenuItemContainerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <motion.div variants={mobileMenuItemVariants}>
                <Link
                  to="/"
                  onClick={closeMobileMenu}
                  className="text-3xl md:text-4xl font-medium hover:opacity-75 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("navbar.home", "Home")}
                </Link>
              </motion.div>
              <motion.div variants={mobileMenuItemVariants}>
                <Link
                  to="/services"
                  onClick={closeMobileMenu}
                  className="text-3xl md:text-4xl font-medium hover:opacity-75 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("navbar.services", "Services")}
                </Link>
              </motion.div>
              <motion.div variants={mobileMenuItemVariants}>
                <Link
                  to="/pricing"
                  onClick={closeMobileMenu}
                  className="text-3xl md:text-4xl font-medium hover:opacity-75 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("navbar.pricing", "Pricing")}
                </Link>
              </motion.div>
              {/* About Us link removed from mobile menu per request - kept here for reference
              <motion.div variants={mobileMenuItemVariants}>
                <Link
                  to="/about"
                  onClick={closeMobileMenu}
                  className="text-3xl md:text-4xl font-medium hover:opacity-75 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("navbar.about", "About")}
                </Link>
              </motion.div>
              */}
              <motion.div variants={mobileMenuItemVariants}>
                <Link
                  to={`/${i18n.language}/blog`}
                  onClick={closeMobileMenu}
                  className="text-3xl md:text-4xl font-medium hover:opacity-75 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("navbar.blog", "Blog")}
                </Link>
              </motion.div>
              <motion.div variants={mobileMenuItemVariants}>
                <Link
                  to="/contact"
                  onClick={closeMobileMenu}
                  className="text-3xl md:text-4xl font-medium hover:opacity-75 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("navbar.contact", "Contact")}
                </Link>
              </motion.div>

              <motion.div
                variants={mobileMenuItemVariants}
                className="flex space-x-5 pt-6"
              >
                {["en", "es", "fr"].map((lng) => (
                  <motion.button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    className={`text-base font-semibold transition-opacity duration-200 ${
                      i18n.language === lng
                        ? "opacity-100 scale-110"
                        : "opacity-50 hover:opacity-100"
                    }`}
                    style={{ color: colors.mobileMenuText }}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {lng.toUpperCase()}
                  </motion.button>
                ))}
              </motion.div>

              <motion.div variants={mobileMenuItemVariants} className="pt-6">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="text-lg font-medium opacity-70 hover:opacity-100 transition-opacity"
                  style={{ color: colors.mobileMenuText }}
                >
                  {t("buttons.login", "Login")}
                </Link>
              </motion.div>
              {/* Botón Sign Up del Menú Móvil Corregido */}
              <motion.div variants={mobileMenuItemVariants} className="pt-8">
                <motion.button
                  onClick={() => {
                    trackSignupClick(i18n.language, "navbar");
                    closeMobileMenu();
                    navigate("/signup");
                  }}
                  className="px-5 py-2 rounded-full text-sm font-semibold border-2 focus:outline-none transition-colors duration-200 hover:bg-white/10"
                  style={{
                    borderColor: colors.mobileMenuButtonBorder,
                    color: colors.mobileMenuButtonText,
                  }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {t("buttons.signUp", "Sign Up")}
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
